import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {useState, useCallback, useMemo} from 'react'
import {parseCronExpression} from '@/lib/cron-utils'
import {Play, X, Plus, Pencil, Check} from 'lucide-react'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {StatusBadge, StatusBadgeType} from '@/components/shared/StatusBadge'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Switch} from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useToast} from '@/hooks/use-toast'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {JobDefinitionControllerJobsEndpoint} from '@/types/endpoint-contracts'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {permissions} from '@/lib/permissions'
import {ErrorBanner} from '@/components/ui/error-banner'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type {JobDefinition} from '@/types/background-jobs'
import {formatTimestamp} from '@/lib/date-utils'
import {CreateJobDialog} from '@/pages/JobBuilderPage'
import {CodeText} from '@/components/shared/CodeText'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {MonoInput} from '@/components/shared/MonoInput'
import {StatusText} from '@/components/shared/StatusText'
import {CompactButton} from '@/components/shared/CompactButton'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {RowHoverButton} from '@/components/shared/RowHoverButton'

// ============================================================================
// Inline Schedule Editor (used in the Schedule column)
// ============================================================================

function InlineScheduleEditor({job, onSaved}: {job: JobDefinition, onSaved: () => void}) {
    const i18n = useI18n()
    const services = useServices()
    const [editing, setEditing] = useState(false)
    const [cronInput, setCronInput] = useState('')
    const removeDialog = useDialog()
    const [error, setError] = useState<Error | null>(null)

    const cronFields = cronInput.trim().split(/\s+/)
    const cronValid = cronInput.trim() === '' || cronFields.length === 6
    const cronParsed = cronFields.length === 6 ? parseCronExpression(cronInput, {use24HourTimeFormat: true}) : {description: null, error: null}
    const cronPreview = cronParsed.description

    const mutation = useEntityMutation({
        mutationFn: (cronExpression: string | null) =>
            services.jobs.schedule(job.id, {cronExpression}),
        successMessage: i18n.scriptEditor.schedule.saved,
        onSuccess: () => {
            setEditing(false)
            setError(null)
            onSaved()
        },
        onError: (err) => setError(err as Error),
    })

    const handleEdit = useCallback(() => {
        setCronInput(job.cronExpression || '')
        setEditing(true)
        setError(null)
    }, [job.cronExpression])

    const handleSave = useCallback(() => {
        if (!cronInput.trim()) {
            removeDialog.open()
            return
        }
        mutation.mutate(cronInput.trim())
    }, [cronInput, mutation, removeDialog])

    const handleCancel = useCallback(() => {
        setEditing(false)
        setError(null)
    }, [])

    const saveDisabled = (!cronValid || (cronInput.trim() !== '' && !cronPreview)) || mutation.isPending

    if (!editing) {
        return (
            <InlineRow gap="xs">
                <CodeText>{job.cronExpression || i18n.jobs.manual}</CodeText>
                <RowHoverButton
                    size="icon-sm"
                    onClick={(e) => {e.stopPropagation(); handleEdit()}}
                    data-testid={`btn-edit-schedule-${job.id}`}
                >
                    <Icon icon={Pencil} size="xs"/>
                </RowHoverButton>
            </InlineRow>
        )
    }

    return (
        <Stack gap="xs" className="min-w-[200px]" onClick={e => e.stopPropagation()}>
            <MonoInput
                inputSize="xs"
                value={cronInput}
                onChange={e => setCronInput(e.target.value)}
                data-testid={`input-cron-${job.id}`}
                autoFocus
            />
            {cronInput.trim() && (
                <StatusText size="xs" tone={cronPreview ? 'success' : 'destructive'}>
                    {cronPreview || i18n.scriptEditor.schedule.invalidCron}
                </StatusText>
            )}
            {error && <ErrorBanner error={error}/>}
            <InlineRow gap="xs">
                <CompactButton
                    variant="default"
                    size="xs"
                    onClick={handleSave}
                    disabled={saveDisabled}
                    data-testid={`btn-save-schedule-${job.id}`}
                >
                    {mutation.isPending ? <ButtonSpinner size="xs" noMargin/> : <Icon icon={Check} size="xs"/>}
                </CompactButton>
                <CompactButton
                    size="xs"
                    onClick={handleCancel}
                    data-testid={`btn-cancel-schedule-${job.id}`}
                >
                    <Icon icon={X} size="xs"/>
                </CompactButton>
            </InlineRow>

            <AlertDialog open={removeDialog.isOpen} onOpenChange={removeDialog.bind.onOpenChange}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.scriptEditor.schedule.removeTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{i18n.scriptEditor.schedule.removeDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{i18n.common.cancel}</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={() => {removeDialog.close(); mutation.mutate(null)}}>
                            {i18n.scriptEditor.schedule.removeConfirm}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Stack>
    )
}

/**
 * Jobs list page — Phase C.6 unified search migration (issue 342.11).
 * Replaces the inline `<SearchInput>` with {@link UnifiedSearchBar}, driven by
 * {@link JobDefinitionControllerJobsEndpoint}: the bar emits `q` against the
 * backend's @Searchable name + description fields; `enabled` (boolean tri-state)
 * and `source` (JobSource enum) render as typed filter chips and panel controls.
 */
export function Jobs() {
    const services = useServices()
    const queryClient = useQueryClient()
    const {hasPermission} = useAuth()
    const i18n = useI18n()
    const {toast} = useToast()
    usePageTitle(i18n.jobs.pageTitle)

    const canAdmin = hasPermission(permissions.jobs.manage)
    const createDialog = useDialog()

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: JobDefinitionControllerJobsEndpoint,
        defaultSortBy: JobDefinitionControllerJobsEndpoint.sort.NAME,
        defaultPageSize: 20,
    })

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const {data: jobsData, isLoading, error} = useQuery({
        queryKey: ['jobs', queryString],
        queryFn: () => services.jobs.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const jobs = jobsData?.content || []
    const totalItems = jobsData?.totalElements || 0
    const totalPages = jobsData?.totalPages || 0

    const toggleMutation = useEntityMutation({
        mutationFn: ({jobId, enabled}: {jobId: string, enabled: boolean}) =>
            services.jobs.update(jobId, {enabled}),
        invalidate: ['jobs'],
        onSuccess: (_, {enabled}) => {
            toast({description: enabled ? i18n.jobs.jobEnabled : i18n.jobs.jobDisabled})
        },
    })

    const triggerMutation = useEntityMutation({
        mutationFn: (jobId: string) => services.jobs.trigger(jobId),
        invalidate: ['jobs'],
        successMessage: i18n.jobs.jobTriggered,
    })

    return (
        <>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.backgroundJob}
                loading={isLoading}
                error={error}
                onRetry={() => queryClient.invalidateQueries({queryKey: ['jobs']})}
                title={i18n.jobs.allJobs}
                count={totalItems}
                data-testid="jobs-page"
                description={i18n.jobs.jobCount({count: totalItems})}
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
                filters={
                    <UnifiedSearchBar
                        contract={JobDefinitionControllerJobsEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.jobs.filterPlaceholder as string}
                    />
                }
                actions={
                    canAdmin ? (
                        <Button
                            onClick={createDialog.open}
                            data-testid="btn-create-job"
                        >
                            <Icon icon={Plus} size="md"/>
                            {i18n.jobs.createJob}
                        </Button>
                    ) : undefined
                }
            >
                <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                <TableHead data-testid="col-source">{i18n.jobs.source}</TableHead>
                                <TableHead data-testid="col-schedule">{i18n.jobs.schedule}</TableHead>
                                {canAdmin && <TableHead data-testid="col-enabled">{i18n.common.enabled}</TableHead>}
                                <TableHead data-testid="col-last-run">{i18n.jobs.lastRun}</TableHead>
                                <TableHead data-testid="col-last-status">{i18n.jobs.lastStatus}</TableHead>
                                <TableHead data-testid="col-next-run">{i18n.jobs.nextRun}</TableHead>
                                <TableHead data-testid="col-actions" className="w-10"/>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {jobs.map((job) => (
                                <TableRow key={job.id} className="group" data-testid={`row-job-${job.id}`}>
                                    <TableCell className="font-medium">
                                        <TextLink
                                            testId={`link-job-detail-${job.id}`}
                                            to={routes.backgroundJobs.jobs.detail(job.id)}
                                        >
                                            {job.name}
                                        </TextLink>
                                    </TableCell>
                                    <TableCell>
                                        <StatusBadge status={job.source || ''} type={StatusBadgeType.SOURCE}/>
                                    </TableCell>
                                    <TableCell>
                                        {canAdmin
                                            ? <InlineScheduleEditor job={job} onSaved={() => queryClient.invalidateQueries({queryKey: ['jobs']})}/>
                                            : <CodeText>{job.cronExpression || i18n.jobs.manual}</CodeText>
                                        }
                                    </TableCell>
                                    {canAdmin && (
                                        <TableCell>
                                            <Switch
                                                checked={job.enabled}
                                                onCheckedChange={(enabled) => toggleMutation.mutate({jobId: job.id, enabled})}
                                                disabled={toggleMutation.isPending}
                                                data-testid={`switch-enabled-${job.id}`}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <MutedText size="sm">{formatTimestamp(job.lastExecutionTime)}</MutedText>
                                    </TableCell>
                                    <TableCell>
                                        <StatusBadge status={job.lastExecutionStatus} type={StatusBadgeType.EXECUTION_STATUS}/>
                                    </TableCell>
                                    <TableCell>
                                        <MutedText size="sm">{formatTimestamp(job.nextRunTime)}</MutedText>
                                    </TableCell>
                                    <TableCell>
                                        {canAdmin && (
                                            <RowHoverButton
                                                data-testid={`btn-trigger-job-${job.id}`}
                                                onClick={() => triggerMutation.mutate(job.id)}
                                                disabled={!job.enabled || triggerMutation.isPending}
                                                title={i18n.jobs.runNow as string}
                                            >
                                                <Icon icon={Play} size="md"/>
                                            </RowHoverButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {jobs.length === 0 && (
                                <TableEmptyState
                                    colSpan={canAdmin ? 8 : 7}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.jobs.noJobsMatch}
                                    emptyMessage={i18n.jobs.noJobsFound}
                                />
                            )}
                        </TableBody>
                    </Table>

            </PageLayout>

            <CreateJobDialog open={createDialog.isOpen} onClose={createDialog.close}/>
        </>
    )
}
